import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { User, Camera, Trash2, Instagram, Facebook, Linkedin, Loader2 } from "lucide-react";
import { FaXTwitter } from "react-icons/fa6";

import userServices from "../../services/userServices";
import { setUser } from "../../stores/slices/authSlices";
import { SetCookie } from "../../utils/utils";

/* =========================================================================
 *  Profile — the signed-in user's account page (/profile).
 *
 *  Lets the user edit display info (username, phone), a short bio, their
 *  social links (Instagram / Facebook / X / LinkedIn — the same fields the
 *  event organizer card surfaces) and their avatar. Email is read-only.
 *  Backed by GET/PUT /user/profile and the profile-image endpoints.
 * ========================================================================= */

const SOCIAL_FIELDS = [
    { key: "instagram", label: "Instagram", Icon: Instagram, placeholder: "@username" },
    { key: "facebook", label: "Facebook", Icon: Facebook, placeholder: "@username" },
    { key: "twitter", label: "X (Twitter)", Icon: FaXTwitter, placeholder: "@username" },
    { key: "linkedin", label: "LinkedIn", Icon: Linkedin, placeholder: "@username" },
];

// Store just the handle: trim, drop a leading "@". A pasted full URL is left
// as-is (the event page treats an http(s) value as a ready-made link).
const cleanHandle = (v) => (v || "").trim().replace(/^@+/, "");

const EMPTY_FORM = {
    username: "",
    phone: "",
    bio: "",
    instagram: "",
    facebook: "",
    twitter: "",
    linkedin: "",
};

export default function Profile() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector((state) => state.auth.user);
    const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);

    const [form, setForm] = useState(EMPTY_FORM);
    const [email, setEmail] = useState("");
    const [image, setImage] = useState(null);
    const [saving, setSaving] = useState(false);
    const [imageBusy, setImageBusy] = useState(false);
    const fileInputRef = useRef(null);

    // Hydrate the form from a fresh profile fetch (the login payload may not
    // include bio / social links). Falls back to the cached redux user.
    const hydrate = (src) => {
        if (!src) return;
        setForm({
            username: src.username || "",
            phone: src.phone || "",
            bio: src.bio || "",
            instagram: src.instagram || "",
            facebook: src.facebook || "",
            twitter: src.twitter || "",
            linkedin: src.linkedin || "",
        });
        setEmail(src.email || "");
        setImage(src.image || null);
    };

    useEffect(() => {
        if (!isLoggedIn) {
            navigate("/");
            return;
        }
        hydrate(user);
        let cancelled = false;
        userServices
            .getProfile()
            .then((res) => {
                if (cancelled) return;
                // GET /user/profile returns the user object as the body.
                const data = res?.data?.user || res?.data || {};
                hydrate({ ...user, ...data });
            })
            .catch(() => {/* keep the cached values */});
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    const onField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    // Persist the updated user into redux + the _aps_u cookie so the header
    // name and other screens stay in sync without a reload.
    const persistUser = (patch) => {
        dispatch(setUser(patch));
        const next = { ...(user || {}), ...patch };
        SetCookie("_aps_u", JSON.stringify(next), 30);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.username.trim()) {
            toast.error("Username is required.");
            return;
        }
        setSaving(true);
        try {
            const res = await userServices.updateProfile({
                username: form.username.trim(),
                phone: form.phone.trim(),
                bio: form.bio.trim(),
                instagram: cleanHandle(form.instagram),
                facebook: cleanHandle(form.facebook),
                twitter: cleanHandle(form.twitter),
                linkedin: cleanHandle(form.linkedin),
            });
            const updated = res?.data?.user || {};
            persistUser(updated);
            toast.success(res?.data?.message || "Profile updated.");
        } catch (err) {
            const msg =
                err?.response?.data?.errors
                    ? Object.values(err.response.data.errors)[0]?.[0]
                    : err?.response?.data?.error || err?.response?.data?.message;
            toast.error(msg || "Could not update profile.");
        } finally {
            setSaving(false);
        }
    };

    const handleImagePick = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // allow re-selecting the same file
        if (!file) return;
        setImageBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await userServices.uploadProfileImage(fd);
            const url = res?.data?.image || null;
            setImage(url);
            persistUser({ image: url });
            toast.success("Profile photo updated.");
        } catch (err) {
            toast.error(err?.response?.data?.error || "Could not upload photo.");
        } finally {
            setImageBusy(false);
        }
    };

    const handleImageRemove = async () => {
        if (!image) return;
        setImageBusy(true);
        try {
            await userServices.deleteProfileImage();
            setImage(null);
            persistUser({ image: null });
            toast.success("Profile photo removed.");
        } catch (err) {
            toast.error(err?.response?.data?.error || "Could not remove photo.");
        } finally {
            setImageBusy(false);
        }
    };

    if (!isLoggedIn) return null;

    const inputClass =
        "w-full bg-white border border-gray-200 rounded-xl py-3 px-4 text-[15px] font-medium text-gray-900 outline-none focus:border-gray-900 transition-colors";
    const labelClass = "block text-sm font-bold text-gray-700 mb-1.5";

    return (
        <div className="min-h-screen bg-gray-50 py-8 md:py-12 animate-fade-in">
            <div className="max-w-2xl mx-auto px-4 sm:px-6">
                <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-1">My Profile</h1>
                <p className="text-gray-500 font-medium mb-8">Manage your account details and public organizer links.</p>

                {/* Avatar */}
                <div className="bg-white rounded-2xl shadow-sm p-6 mb-6 flex items-center gap-5">
                    <div className="relative w-20 h-20 shrink-0">
                        <div className="w-20 h-20 rounded-full bg-[#ffea00] flex items-center justify-center overflow-hidden border-2 border-white shadow-sm">
                            {image ? (
                                <img src={image} alt="Profile" className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-3xl font-black text-black">
                                    {(form.username || email || "?").charAt(0).toUpperCase()}
                                </span>
                            )}
                        </div>
                        {imageBusy && (
                            <div className="absolute inset-0 rounded-full bg-white/60 flex items-center justify-center">
                                <Loader2 size={22} className="animate-spin text-gray-700" />
                            </div>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={imageBusy}
                            className="inline-flex items-center gap-2 bg-black hover:bg-gray-800 text-white font-bold text-sm px-4 py-2.5 rounded-xl border-none transition-colors disabled:opacity-50"
                        >
                            <Camera size={16} /> {image ? "Change photo" : "Upload photo"}
                        </button>
                        {image && (
                            <button
                                type="button"
                                onClick={handleImageRemove}
                                disabled={imageBusy}
                                className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-sm px-4 py-2.5 rounded-xl border-none transition-colors disabled:opacity-50"
                            >
                                <Trash2 size={16} /> Remove
                            </button>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                            className="hidden"
                            onChange={handleImagePick}
                        />
                    </div>
                </div>

                <form onSubmit={handleSave} className="bg-white rounded-2xl shadow-sm p-6 space-y-5">
                    <div>
                        <label className={labelClass}>Email</label>
                        <input value={email} disabled className={`${inputClass} bg-gray-50 text-gray-400 cursor-not-allowed`} />
                    </div>
                    <div>
                        <label className={labelClass}>Username</label>
                        <input value={form.username} onChange={onField("username")} className={inputClass} placeholder="Your display name" />
                    </div>
                    <div>
                        <label className={labelClass}>Phone</label>
                        <input value={form.phone} onChange={onField("phone")} className={inputClass} placeholder="+1 555 123 4567" />
                    </div>
                    <div>
                        <label className={labelClass}>Bio</label>
                        <textarea
                            value={form.bio}
                            onChange={onField("bio")}
                            rows={4}
                            maxLength={1000}
                            className={`${inputClass} resize-y`}
                            placeholder="A short blurb shown on your organizer card."
                        />
                        <p className="text-xs text-gray-400 font-medium mt-1 text-right">{form.bio.length}/1000</p>
                    </div>

                    <div className="pt-2">
                        <h3 className="text-sm font-black text-gray-900 uppercase tracking-wide mb-1">Social links</h3>
                        <p className="text-xs text-gray-500 font-medium mb-4">Shown as clickable icons on your event pages. Leave blank to hide.</p>
                        <div className="space-y-4">
                            {SOCIAL_FIELDS.map(({ key, label, Icon, placeholder }) => (
                                <div key={key}>
                                    <label className={`${labelClass} flex items-center gap-2`}>
                                        <Icon size={15} /> {label}
                                    </label>
                                    <input value={form[key]} onChange={onField(key)} className={inputClass} placeholder={placeholder} />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="pt-2 flex justify-end">
                        <button
                            type="submit"
                            disabled={saving}
                            className="inline-flex items-center gap-2 bg-[#ffea00] hover:bg-[#e6d300] text-black font-black px-7 py-3 rounded-xl border-none shadow-sm transition-all disabled:opacity-60"
                        >
                            {saving && <Loader2 size={16} className="animate-spin" />}
                            {saving ? "Saving…" : "Save changes"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
